import { useEffect, useMemo, useState } from 'react';
import type {
  PropertySharingExternalSite,
  PropertySharingFieldPart,
  PropertySharingIdentifierPart,
} from 'src/api/generated';
import { usePropertySharingPreview } from 'src/modules/shared/hooks/api/catalog/property-sharing';
import { propertyLinksFormSchema } from '../validations/property-links.validation';
import {
  propertyLinkExampleValue,
  propertyLinkParameterKey,
  propertyLinkParameters,
} from '../utils/property-link-template.util';

/**
 * Keep fictional input separate from policy and request a debounced backend preview.
 * @param site - Current external-link template.
 * @returns Sample fields, preview state and ready-to-use edit/retry actions.
 */
export function usePropertyLinkExamplePreview(
  site: PropertySharingExternalSite
) {
  const parameters = useMemo(() => propertyLinkParameters(site), [site]);
  const [examples, setExamples] = useState<Record<string, string>>({});
  const values = parameters.map((parameter) => ({
    parameter,
    value:
      examples[propertyLinkParameterKey(parameter)] ??
      propertyLinkExampleValue(parameter),
  }));
  const serialized = JSON.stringify({ site, values });
  const [settled, setSettled] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSettled(serialized), 350);
    return () => clearTimeout(timer);
  }, [serialized]);
  const validated = propertyLinksFormSchema.shape.externalSite.safeParse(site);
  const ready =
    settled === serialized && validated.success && validated.data !== null;
  const preview = usePropertySharingPreview(
    ready && validated.data !== null
      ? { externalSite: validated.data, values }
      : null
  );

  return {
    available: parameters.length > 0 && validated.success,
    values,
    loading: !ready || preview.isFetching,
    error: ready && preview.isError,
    candidate: ready && !preview.isFetching ? preview.data : undefined,
    setExampleValue: (
      parameter: PropertySharingFieldPart | PropertySharingIdentifierPart,
      value: string
    ) =>
      setExamples((current) => ({
        ...current,
        [propertyLinkParameterKey(parameter)]: value,
      })),
    retry: () => {
      void preview.refetch();
    },
  };
}
